import FocusTrap from '@utils/focus-trap';
import isFn from '@utils/is-function';
import keyCodes from '@utils/key-codes';
import sharedParent from '@utils/shared-parent';
import ApiService from '@api/api-service';

const EVENT_KEY = '_headerModals_';
const EVENT_CLICK = `click.${EVENT_KEY}`;
const EVENT_KEYUP = `keyup.${EVENT_KEY}`;
const EVENT_KEYUP_ESC = `${EVENT_KEYUP}.esc`;
const EVENT_MOUSE_ENTER = `mouseenter.${EVENT_KEY}`;
const EVENT_MOUSE_LEAVE = `mouseleave.${EVENT_KEY}`;

var VCA = VCA || {};

VCA.HeaderModals = (function ($) {

    const ui = {};

    let activeModalId = null;
    let focusTrap = null;
    let hidingModalTimeout = null
    let queuedHideModalFn = null;
    let isStatic = false;
    let emergencyModalIsLoaded = false;

    function cacheUI() {
        ui.$document = $(document);
        ui.$headerModals = $('.header-btn-modals');
        ui.$loggedInModal = $('#myVCALoggedInModal');
        ui.$emergencyBtn;

        ui.$headerModalElements = $.map(ui.$headerModals.toArray(), (...[, index]) => {
            const $this = ui.$headerModals.eq(index);
            const id = '#' + $this.data('modal_id');
            const $modalEl = $(id);
            let $parent = sharedParent($this, $modalEl);
            let $hoverEl = $parent.length ? $parent : $this;
            if(id === "#myVCALoginModal"){
                $hoverEl = $this;
            }

            const $triggerEl = $this.is('button') ? $this : null;
            return {
                id,
                $modalEl,
                $hoverEl,
                ...($triggerEl && { $triggerEl } || { $triggerEl: $() })
            };
        });
    }

    function bindEvents() {
        addHoverListeners();

        ui.$headerModalElements.forEach((modal) => {
            modal.$triggerEl.on(EVENT_CLICK, () => toggleStaticModal(modal));
            modal.$triggerEl.on(EVENT_KEYUP, (evt) => onTriggerKeyup(evt, modal));
        });

        $(window).on('resize', detectWindowHeight);
    }

    function unbindEvents() {
        ui.$headerModalElements.forEach((modal) => {
            modal.$triggerEl.unbind();
            modal.$hoverEl.unbind();
        });
    }

    function addHoverListeners() {
        ui.$headerModalElements.forEach((modal) => {
            modal.$hoverEl
                .on(EVENT_MOUSE_ENTER, () => showModal(modal))
                .on(EVENT_MOUSE_LEAVE, () => queueModalTimeout(modal));
        });
    }

    function removeHoverListeners() {
        ui.$headerModalElements.forEach(({ $hoverEl }) => {
            $hoverEl
                .off(EVENT_MOUSE_ENTER)
                .off(EVENT_MOUSE_LEAVE);
        });
    }

    function onClickOut(evt) {
        if(hidingModalTimeout) {
            skipTimeout();
            return;
        }

        const $target = $(evt.target);
        const { $hoverEl } = ui.$headerModalElements.find(({ id }) => id === activeModalId) || {};

        if ($hoverEl) {
            if (!$target.closest($hoverEl).length) {
                if($($hoverEl).attr('id') == 'myVCALoginModal'){
                    if (!$target.closest('#myVCALoginBtn').length){
                        hideStaticModal();
                    }
                }else{
                    hideStaticModal();
                }

            }
        }
    }

    function onTriggerKeyup(evt, modal) {
        if (isStatic) {
            return true;
        }

        const $target = $(evt.target);
        const key = evt.which ? evt.which : evt.keyCode;
        const keyIndex = [ keyCodes.ARROW_UP, keyCodes.ARROW_DOWN ].indexOf(key);

        if (keyIndex > -1) {
            showStaticModal(modal);

            if(keyIndex && focusTrap?.$current.is($target)) {
                focusTrap.next();
            } else {
                focusTrap.last();
            }
        }
    }

    function toggleStaticModal({ id }) {
        if (activeModalId !== id || !isStatic) {
            showStaticModal(...arguments)
        } else {
            hideStaticModal();
        }
    }

    function showStaticModal(modal) {
        isStatic = true;
        showModal(modal);
        removeHoverListeners();

        focusTrap = new FocusTrap(modal.$hoverEl, {
            allowBreakout: true,
            nextKeys: [keyCodes.ARROW_DOWN],
            prevKeys: [keyCodes.ARROW_UP],
            onBreakout() {
                hideStaticModal();
            }
        });

        focusTrap.$focusable.on(EVENT_KEYUP_ESC, (evt) => {
            const key = evt.which ? evt.which : evt.keyCode;
            if (key === keyCodes.ESCAPE) {
                focusTrap.$focusable.off(EVENT_KEYUP_ESC);
                hideStaticModal();
            }
        });

        queuedHideModalFn = () => {
            addHoverListeners();
            hideModal(modal);
        };
    }

    function hideStaticModal() {
        isStatic = false;
        activeModalId = null;
        focusTrap?.destroy();
        focusTrap = null;

        if (isFn(queuedHideModalFn)) {
            queuedHideModalFn();
        }
    }

    function showModal({ id, $modalEl }) {
        activeModalId = id;

        skipTimeout();

        $modalEl.fadeIn('fast');

        ui.$document.on(EVENT_CLICK, (evt) => onClickOut(evt));
    }

    function hideModal({ id, $modalEl }) {
        if (activeModalId !== id) {
            $modalEl.fadeOut('fast', () => {
                if (activeModalId === null) {
                    ui.$document.off(EVENT_CLICK);
                }
            });
        }

        ui.$closingModal = null;
        queuedHideModalFn = null;
        hidingModalTimeout = null;
    }

    function queueModalTimeout(modal) {
        ui.$closingModal = modal.$modalEl;
        activeModalId = null;

        queuedHideModalFn = () => hideModal(modal);
        hidingModalTimeout = setTimeout(queuedHideModalFn, 500);
    }

    function skipTimeout() {
        if (hidingModalTimeout) {
            clearTimeout(hidingModalTimeout);
        }

        if (isFn(queuedHideModalFn)) {
            queuedHideModalFn();
            queuedHideModalFn = null;
        }
    }

    function createDesktopEmergencyInfoModal() {
        let emergencyContainer = $('#emergencyModalContainer');
        if (!emergencyContainer.length) {
            return;
        }

        emergencyContainer.append(
            ` <button 
                type="button" 
                id="EmergencyBtn"
                class="header-btn-modals action-btn__emergencyBtn" 
                aria-label="Emergency"
                data-modal_id="EmergencyModal">
                <img class="hover-off" src="/images-2/emergency-default.png" alt="">
                <img class="hover-on" src="/images-2/emergency-hover.png" alt="">
            </button>`
        );

        bindEmergencyBtn();
    }

    function bindEmergencyBtn() {
        ui.$emergencyBtn = $('#EmergencyBtn');

        ui.$emergencyBtn.on('click', function (e) {
            e.preventDefault();
            getEmergencyModalInfo();
        });
    }

    async function getEmergencyModalInfo() {
        if (emergencyModalIsLoaded) {
            return;
        }

        let emergencyContainer = $('#emergencyModalContainer');

        ui.$emergencyBtn.prop('disabled', true);
        emergencyContainer.find('#EmergencyModal').remove();

        let api = new ApiService('/emergency-api/get-emergency-modal');

        var pathName = '';
        let currentUrl = new URL(document.URL);  
        if(currentUrl !== null)
        {
            pathName = currentUrl.pathname;
        }

        let query = {
            contextItemId: emergencyContainer.data('context-item'),
            emergencyCTAItemId: emergencyContainer.data('emergency-cta-item'),
            urlPath: pathName 
        }
        const { data, success, error } = await api.query(query);

        if (success) {
            if (data) {

                var phones = '';
                $.each(data.phone, function (i, item) {
                    if(item !== '')
                    {
                        if(i != 0) {
                            phones += `<div><a class="emergency-modal__tel" href="tel:${item}">Alt: ${item}</a></div>`;
                        }else{
                            phones += `<div><a class="emergency-modal__tel" href="tel:${item}">${item}</a></div>`;
                        }
                    }
                });

                emergencyContainer.append(
                    `<div 
                        id="EmergencyModal" 
                        class="header-modal emergency header-btn-modals"
                        data-modal_id="EmergencyModal" 
                        aria-labelledby="EmergencyBtn">

                        <div class="emergency-modal">
                            <img class="emergency-modal__logo" src="${data.iconUrl}" alt="Emergency Care Icon"></img>
                            <div class="emergency-modal__title">${data.title}</div>
                            ${phones}
                            <div class="emergency-modal__details">${data.message}</div>
                        </div>
                    </div>`
                );
                emergencyModalIsLoaded = true;
            }
            ui.$emergencyBtn.prop('disabled', false);
        } else  {
            emergencyContainer.append(`
                <div 
                    id="EmergencyModal" 
                    class="header-modal emergency header-btn-modals"
                    data-modal_id="EmergencyModal" 
                    aria-labelledby="EmergencyBtn">

                    <div class="emergency-modal">
                        <div class="desktop-emergency-error">${error}</div>
                    </div>
                </div>
            `);
            ui.$emergencyBtn.prop('disabled', false);
            console.log("Error retrieving emergency modal information: ", error);
        }
        cacheUI();
        unbindEvents();
        bindEvents();
    }

    function detectWindowHeight() {
        let windowHeight = $(window).height();
        
        if (windowHeight < 700) {
            ui.$loggedInModal.find('.myvca-loggedin-modal').addClass('-short');
        }
    }

    function init() {
        createDesktopEmergencyInfoModal();
        cacheUI();
        bindEvents();
        detectWindowHeight();
    }

    return {
        init: init
    };

})(jQuery);

$(function () {
    VCA.HeaderModals.init();
});
